import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Merge, Loader2, Undo2 } from "lucide-react";
import { fmtTL } from "@/lib/utils";

const LABEL = { firm: { one: "Firma", btn: "Firma Birleştir" }, project: { one: "Proje", btn: "Proje Birleştir" } };

export default function MergeDialog({ type, entities, onMerged }) {
  const [open, setOpen] = useState(false);
  const [suggest, setSuggest] = useState([]);
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState(false);
  const L = LABEL[type];

  const load = () => {
    api.get("/merge/suggestions", { params: { type } }).then(r => setSuggest(r.data));
    api.get("/merge/rules", { params: { type } }).then(r => setRules(r.data));
  };
  useEffect(() => { if (open) load(); }, [open]);

  const merge = async (ids, targetId, name) => {
    setBusy(true);
    try {
      await api.post("/merge", { type, ids, target_id: targetId, name });
      toast.success(`${ids.length} kayıt "${name}" altında birleştirildi`);
      load(); onMerged();
    } catch (e) { toast.error(e.response?.data?.detail || "Birleştirme başarısız"); }
    finally { setBusy(false); }
  };

  const unmerge = async (rule) => {
    if (!window.confirm(`"${rule.name}" birleştirmesi geri alınsın mı? Veriler kayıtlı Excel'den yeniden aktarılacak.`)) return;
    setBusy(true);
    try { await api.delete(`/merge/rules/${rule.id}`); toast.success("Birleştirme geri alındı"); load(); onMerged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Geri alınamadı"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid={`open-merge-${type}`}><Merge className="w-4 h-4 mr-1" /> {L.btn}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`merge-dialog-${type}`}>
        <DialogHeader><DialogTitle className="font-display">{L.btn}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">Birleştirilen kayıtların tüm hakediş, iş kalemi ve tutarları tek {L.one.toLowerCase()} altında toplanır. Birleştirmeler yeni Excel aktarımlarında da korunur.</p>
        {busy && <div className="flex items-center gap-2 text-sm text-amber-600"><Loader2 className="w-4 h-4 animate-spin" /> İşleniyor...</div>}
        <Tabs defaultValue="suggest">
          <TabsList>
            <TabsTrigger value="suggest" data-testid="merge-tab-suggest">Öneriler ({suggest.length})</TabsTrigger>
            <TabsTrigger value="manual" data-testid="merge-tab-manual">Elle Seç</TabsTrigger>
            <TabsTrigger value="rules" data-testid="merge-tab-rules">Birleştirilmişler ({rules.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="suggest" className="space-y-3">
            {suggest.length === 0 && <p className="text-sm text-muted-foreground py-4">Benzer kayıt önerisi yok.</p>}
            {suggest.map(g => <MergeGroup key={g.label + g.items.map(i => i.id).join()} group={g} onMerge={merge} disabled={busy} />)}
          </TabsContent>
          <TabsContent value="manual">
            <ManualMerge entities={entities} onMerge={merge} disabled={busy} one={L.one} />
          </TabsContent>
          <TabsContent value="rules" className="space-y-2">
            {rules.length === 0 && <p className="text-sm text-muted-foreground py-4">Henüz birleştirme yapılmadı.</p>}
            {rules.map(r => (
              <div key={r.id} className="rounded-lg border border-border p-3 flex items-start justify-between gap-3" data-testid={`merge-rule-${r.id}`}>
                <div>
                  <div className="font-semibold text-sm">{r.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{r.source_names.join(" • ")}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => unmerge(r)} disabled={busy} data-testid={`unmerge-${r.id}`}><Undo2 className="w-4 h-4 mr-1" /> Ayır</Button>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function MergeGroup({ group, onMerge, disabled }) {
  const [checked, setChecked] = useState(group.items.map(i => i.id));
  const [target, setTarget] = useState(group.items[0].id);
  const [name, setName] = useState(group.items[0].name);
  const pick = (it) => { setTarget(it.id); setName(it.name); if (!checked.includes(it.id)) setChecked([...checked, it.id]); };
  const total = group.items.filter(i => checked.includes(i.id)).reduce((s, i) => s + (i.total_amount || 0), 0);

  return (
    <div className="rounded-lg border border-border p-4" data-testid={`merge-group-${group.label}`}>
      <div className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2">{group.label}</div>
      <div className="space-y-1.5">
        {group.items.map(it => (
          <div key={it.id} className="flex items-center gap-3 text-sm">
            <Checkbox checked={checked.includes(it.id)} onCheckedChange={(v) => setChecked(v ? [...checked, it.id] : checked.filter(x => x !== it.id))} />
            <button type="button" onClick={() => pick(it)} className={`flex-1 text-left ${target === it.id ? "font-semibold" : ""}`}>
              {it.name} {target === it.id && <span className="text-[10px] ml-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-700 dark:text-amber-400">hedef</span>}
            </button>
            <span className="font-mono-nums text-xs text-muted-foreground">{fmtTL(it.total_amount)}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3 items-center">
        <Input value={name} onChange={e => setName(e.target.value)} className="flex-1" placeholder="Birleşik isim" />
        <Button size="sm" disabled={disabled || checked.length < 2 || !checked.includes(target)} onClick={() => onMerge(checked, target, name)}
          className="bg-amber-500 hover:bg-amber-600 text-slate-950" data-testid="merge-group-button">Birleştir ({fmtTL(total)})</Button>
      </div>
    </div>
  );
}

function ManualMerge({ entities, onMerge, disabled, one }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [target, setTarget] = useState("");
  const [name, setName] = useState("");
  const list = entities.filter(e => e.name.toLocaleUpperCase("tr").includes(q.toLocaleUpperCase("tr")));
  const toggle = (e, v) => {
    const next = v ? [...sel, e.id] : sel.filter(x => x !== e.id);
    setSel(next);
    if (v && !target) { setTarget(e.id); setName(e.name); }
    if (!v && target === e.id) { const t = entities.find(x => x.id === next[0]); setTarget(t?.id || ""); setName(t?.name || ""); }
  };

  return (
    <div>
      <Input placeholder={`${one} ara...`} value={q} onChange={e => setQ(e.target.value)} className="mb-3" data-testid="manual-merge-search" />
      <div className="max-h-72 overflow-y-auto rounded-lg border border-border divide-y divide-border">
        {list.map(e => (
          <label key={e.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-muted/40">
            <Checkbox checked={sel.includes(e.id)} onCheckedChange={(v) => toggle(e, v)} data-testid={`manual-merge-item-${e.id}`} />
            <span className="flex-1">{e.name}</span>
            {sel.includes(e.id) && (
              <button type="button" onClick={(ev) => { ev.preventDefault(); setTarget(e.id); setName(e.name); }}
                className={`text-[10px] px-1.5 py-0.5 rounded ${target === e.id ? "bg-amber-500 text-slate-950" : "bg-muted"}`}>{target === e.id ? "hedef" : "hedef yap"}</button>
            )}
            <span className="font-mono-nums text-xs text-muted-foreground">{fmtTL(e.total_amount)}</span>
          </label>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="Birleşik isim" className="flex-1" data-testid="manual-merge-name" />
        <Button disabled={disabled || sel.length < 2} onClick={() => { onMerge(sel, target, name); setSel([]); setTarget(""); setName(""); }}
          className="bg-amber-500 hover:bg-amber-600 text-slate-950" data-testid="manual-merge-button">{sel.length} kaydı birleştir</Button>
      </div>
    </div>
  );
}
